/**
 * Deterministic fixtures for marketing-page bento widgets (production
 * pages and design-lab mockups both import from here). Same discipline as
 * the design lab: no Math.random(), no `new Date()`. People and companies
 * are fictional; numbers are invented-but-realistic. Sample data stays
 * English in both locales (it plays the role a product screenshot would);
 * widget chrome is translated via the page's i18n namespace.
 */

export type PipelineStageKey = "fanout" | "ranking" | "read" | "scored";

export const pipelineStages: Array<{
	key: PipelineStageKey;
	label: string;
	blurb: string;
}> = [
	{
		key: "fanout",
		label: "Query fan-out",
		blurb: "AI turns one prompt into several web searches",
	},
	{
		key: "ranking",
		label: "Search ranking",
		blurb: "Your page has to rank for those searches",
	},
	{
		key: "read",
		label: "Content read",
		blurb: "AI fetches the page and extracts what it can",
	},
	{
		key: "scored",
		label: "Relevance scored",
		blurb: "Extracted passages compete for the answer",
	},
];

/**
 * Pages flowing through the pipeline. `exitStage` is the stage index the page
 * falls out at (0-based against pipelineStages); survivors have exitStage 4
 * (past the last stage = cited in the answer).
 */
export const pipelinePages: Array<{
	id: string;
	page: string;
	exitStage: number;
	exitReason: string | null;
}> = [
	{
		id: "guide",
		page: "/guides/tent-buying-guide",
		exitStage: 4,
		exitReason: null,
	},
	{
		id: "comparison",
		page: "/compare/ridgeline-vs-bastion",
		exitStage: 3,
		exitReason: "Outscored by a fresher comparison",
	},
	{
		id: "blog",
		page: "/blog/winter-camping-tips",
		exitStage: 2,
		exitReason: "Thin extractable copy",
	},
	{
		id: "product",
		page: "/products/ridgeline-2",
		exitStage: 4,
		exitReason: null,
	},
	{
		id: "faq",
		page: "/faq/tent-care",
		exitStage: 1,
		exitReason: "Doesn't rank for the fan-out queries",
	},
	{
		id: "press",
		page: "/press/2026-gear-awards",
		exitStage: 0,
		exitReason: "Not matched to any fan-out query",
	},
];

/** Coverage funnel counts for the PR page (coverage → cited → inline). */
export const coverageFunnel = { placements: 32, cited: 14, citedInline: 6 };

/** Per-client usage rows for the agencies #client-reporting tile. */
export const clientUsage: Array<{
	label: string;
	sublabel: string;
	value: number;
}> = [
	{
		label: "Acme Outdoor Co.",
		sublabel: "1,240 credits · 18 prompts",
		value: 1240,
	},
	{
		label: "Meridian Analytics",
		sublabel: "860 credits · 12 prompts",
		value: 860,
	},
	{
		label: "Wildpath Supply",
		sublabel: "410 credits · 6 prompts",
		value: 410,
	},
];

/** Author rows for the PR #author-tracking tile (citations of their work). */
export const authorRows: Array<{
	label: string;
	sublabel: string;
	value: number;
}> = [
	{ label: "Riley Chen", sublabel: "SNEWS · trade press", value: 12 },
	{ label: "Sam Okafor", sublabel: "OutdoorGearLab · reviews", value: 9 },
	{ label: "J. Marsh", sublabel: "Switchback Travel · guides", value: 7 },
];

/** Worst-pages rows for the readiness-audit hero tile. */
export const worstPages: Array<{ url: string; score: number; issue: string }> =
	[
		{ url: "/legal/terms", score: 19, issue: "JavaScript-only content" },
		{ url: "/docs/getting-started", score: 38, issue: "Structural desert" },
		{ url: "/blog/how-ai-cites", score: 52, issue: "No author or dates" },
		{
			url: "/ai-visibility-report",
			score: 64,
			issue: "Missing structured data",
		},
	];

/** Hero SoV rows for the home proof bento (fictional brands; Acme = self). */
export const homeSovEntries: Array<{
	name: string;
	share: number;
	isSelf?: boolean;
}> = [
	{ name: "SummitGear", share: 38.2 },
	{ name: "Acme Outdoor Co.", share: 31.4, isSelf: true },
	{ name: "TrailForge", share: 24.6 },
	{ name: "Peakline", share: 19.3 },
];

/** Hero AI-traffic stats with pre-plotted 14-day mini series (0-100). */
export const homeTraffic = {
	agents: {
		total: 934,
		trend: 18.2,
		series: [22, 30, 26, 38, 34, 46, 41, 55, 49, 63, 58, 74, 69, 88],
	},
	clickthroughs: {
		total: 261,
		trend: 9.4,
		series: [30, 26, 35, 31, 42, 37, 48, 44, 55, 50, 62, 57, 70, 66],
	},
};

/** Before/after scores for the rewrite loop tiles. */
export const rewriteLoop = {
	before: 41,
	after: 78,
	rewrites: 2,
	page: "/blog/winter-camping-tips",
};

/** Fan-out queries with SERP position — the Grounding Search Rankings tile. */
export const fanoutQueries: Array<{
	query: string;
	rank: number | null;
	platforms: number;
}> = [
	{ query: "best 4-season tents 2026", rank: 3, platforms: 4 },
	{ query: "ultralight tent comparison", rank: 7, platforms: 3 },
	{ query: "tent waterproofing ratings explained", rank: 1, platforms: 2 },
	{ query: "expedition tent wind rating", rank: null, platforms: 3 },
	{ query: "budget backpacking tent reviews", rank: 12, platforms: 2 },
];

/** The three metric tiers for MetricTiersBento (prompt-tracking story). */
export const metricTiers = {
	kpi: {
		label: "Share of goal influence",
		value: 42.3,
		delta: 5.1,
		caption: "vs previous 7 days · 41 prompts daily",
	},
	backing: [
		{ goal: "Gear review roundups", citations: 24, influence: 18.5 },
		{ goal: "/guides/* page group", citations: 19, influence: 14.6 },
		{ goal: "Outdoor trade press", citations: 12, influence: 9.2 },
	],
	raw: [
		{
			prompt: "best 4-season tents for alpine conditions",
			platform: "ChatGPT",
			citation: "outdoorgearlab.com/tent-roundup",
			date: "Jul 20",
		},
		{
			prompt: "which tent brands have the best warranty",
			platform: "Perplexity",
			citation: "acmeoutdoor.example/guides/warranty",
			date: "Jul 20",
		},
		{
			prompt: "ultralight backpacking setup for beginners",
			platform: "Gemini",
			citation: "switchbacktravel.example/ultralight",
			date: "Jul 19",
		},
	],
};

/** Project goal types shown on /prompt-tracking (#goals). */
export const goalExamples: Array<{
	kind: string;
	target: string;
	hits: number;
	influence: number;
}> = [
	{ kind: "Page group", target: "/guides/*", hits: 19, influence: 14.6 },
	{ kind: "Publisher", target: "OutdoorGearLab", hits: 24, influence: 18.5 },
	{
		kind: "Article",
		target: "“Best tents of 2026” roundup",
		hits: 9,
		influence: 6.9,
	},
	{
		kind: "Author",
		target: "Riley Chen (trade press)",
		hits: 5,
		influence: 3.8,
	},
	{
		kind: "Coverage group",
		target: "Spring launch coverage (32 URLs)",
		hits: 12,
		influence: 9.2,
	},
];

/** Ranked share rows — locations (SoV by market). */
export const marketShares: Array<{
	label: string;
	sublabel: string;
	value: number;
}> = [
	{ label: "Austin", sublabel: "TX · 42 mentions", value: 34.8 },
	{ label: "Denver", sublabel: "CO · 37 mentions", value: 29.1 },
	{ label: "Portland", sublabel: "OR · 25 mentions", value: 22.6 },
	{ label: "Seattle", sublabel: "WA · 21 mentions", value: 18.0 },
];

/** Ranked share rows — products (recommendation share). */
export const productShares: Array<{
	label: string;
	sublabel: string;
	value: number;
}> = [
	{ label: "Ridgeline 2 Tent", sublabel: "vs 5 tracked rivals", value: 31.4 },
	{
		label: "Featherlite Sleep System",
		sublabel: "vs 4 tracked rivals",
		value: 24.9,
	},
	{ label: "Nimbus 55 Pack", sublabel: "vs 6 tracked rivals", value: 17.2 },
];

/** Ranked value rows — publishers by AIPVS (citation intelligence). */
export const publisherValues: Array<{
	label: string;
	sublabel: string;
	value: number;
}> = [
	{ label: "OutdoorGearLab", sublabel: "cited 24× this month", value: 87 },
	{ label: "Switchback Travel", sublabel: "cited 17× this month", value: 81 },
	{
		label: "Trade press (SNEWS)",
		sublabel: "cited 6× this month",
		value: 64,
	},
	{ label: "Niche forums", sublabel: "cited 11× this month", value: 42 },
];

/** Hidden-earned-media examples (content mention, never credited). */
export const hiddenMediaItems: Array<{
	page: string;
	note: string;
	kind: "hidden" | "competitor";
}> = [
	{
		page: "switchbacktravel.example/best-tents-2026",
		note: "Names the brand in its comparison table; AI read it in 9 answers and never credited it once.",
		kind: "hidden",
	},
	{
		page: "gearjunkie.example/4-season-guide",
		note: "Cited inline 6 times, but only to back a competitor's claim.",
		kind: "competitor",
	},
];

/** Citation classification mix over 4 weeks (share of citations, %). */
export const citationMix: Array<{
	week: string;
	attributed: number;
	contentMention: number;
	unrelated: number;
}> = [
	{ week: "Jun 29", attributed: 34, contentMention: 22, unrelated: 44 },
	{ week: "Jul 6", attributed: 37, contentMention: 24, unrelated: 39 },
	{ week: "Jul 13", attributed: 41, contentMention: 21, unrelated: 38 },
	{ week: "Jul 20", attributed: 46, contentMention: 19, unrelated: 35 },
];

/** MCP terminal conversation for the /mcp-server + /prompt-tracking tiles. */
export const mcpConversation: Array<{
	role: "user" | "assistant";
	text: string;
}> = [
	{
		role: "user",
		text: "Which publishers drove our citations last month?",
	},
	{
		role: "assistant",
		text: "Top sources: OutdoorGearLab (24), Switchback Travel (17)… AIPVS-weighted, review platforms drove 61% of attributed citations.",
	},
	{
		role: "user",
		text: "Export the raw rows for the /guides/* goal as CSV.",
	},
	{
		role: "assistant",
		text: "19 citations · prompt, platform, date, answer context → guides-goal-citations.csv",
	},
];

export const personSnapshot = {
	name: "Dana Whitfield",
	initials: "DW",
	role: "CEO & Co-founder",
	company: "Meridian Analytics",
	location: "Pittsburgh, PA",
	knownFor: [
		"Founded Meridian Analytics in 2019",
		'Author of "Signals Over Noise" (2024)',
		"Frequent speaker on data ethics",
	],
	sources: "Built from Wikidata, Wikipedia, and public coverage",
};

export const identityAccuracy = {
	score: 84,
	delta: 6,
	perPlatform: [
		{
			key: "chatgpt",
			label: "ChatGPT",
			withBrowsing: 92,
			withoutBrowsing: 78,
		},
		{
			key: "gemini",
			label: "Gemini",
			withBrowsing: 88,
			withoutBrowsing: 71,
		},
		{
			key: "claude",
			label: "Claude",
			withBrowsing: 90,
			withoutBrowsing: 82,
		},
		{
			key: "perplexity",
			label: "Perplexity",
			withBrowsing: 86,
			withoutBrowsing: 69,
		},
	],
};

export const confusionFlags: Array<{
	platform: string;
	kind: "same-name" | "outdated";
	claim: string;
	note: string;
}> = [
	{
		platform: "Gemini",
		kind: "same-name",
		claim: "“Dana Whitfield is a marine biologist at Pacific Coast University”",
		note: "Blended with a different Dana Whitfield. Flagged in 2 of 8 answers.",
	},
	{
		platform: "ChatGPT",
		kind: "outdated",
		claim: "“…currently VP of Product at Harborview Software”",
		note: "Role ended in 2019. Appears only in no-browsing answers.",
	},
];

/**
 * Tracked narrative attributes with nightly mention rates — mirrors the
 * person report's tracked positive/negative attributes surface. Negative
 * attributes are lower-is-better.
 */
export const personAttributes: Array<{
	message: string;
	sentiment: "positive" | "negative";
	mentionRate: number;
	trend: number;
}> = [
	{
		message: "Founded Meridian Analytics",
		sentiment: "positive",
		mentionRate: 92,
		trend: 3,
	},
	{
		message: "Author of “Signals Over Noise”",
		sentiment: "positive",
		mentionRate: 64,
		trend: 11,
	},
	{
		message: "Authority on data ethics",
		sentiment: "positive",
		mentionRate: 41,
		trend: 6,
	},
	{
		message: "Linked to the 2021 Harborview layoffs",
		sentiment: "negative",
		mentionRate: 9,
		trend: -4,
	},
];

/**
 * Ranked share rows — a tracked CATEGORY with no focal brand. Share of
 * answers, so the values do not sum to 100: an answer can name several of
 * these brands, and most name none of the long tail. Fictional brands.
 */
export const categoryShares: Array<{
	label: string;
	sublabel: string;
	value: number;
}> = [
	{ label: "Auralis", sublabel: "612 answers", value: 47.8 },
	{ label: "Northwave Audio", sublabel: "431 answers", value: 33.7 },
	{ label: "Cadence Labs", sublabel: "288 answers", value: 22.5 },
	{ label: "Halden Audio", sublabel: "173 answers", value: 13.5 },
	{ label: "Piper Sound", sublabel: "96 answers", value: 7.5 },
];

/** The same category broken out by market: each one has its own leader. */
export const categoryMarkets: Array<{
	name: string;
	leader: string;
	share: number;
}> = [
	{ name: "United States", leader: "Auralis", share: 49.6 },
	{ name: "United Kingdom", leader: "Northwave Audio", share: 41.2 },
	{ name: "Germany", leader: "Halden Audio", share: 38.4 },
];

// ── Media Lists + Coverage Reports (PR product pages) ──────────────────────
//
// The /media-list and /coverage-report pages and the /for-pr-firms workflow
// block (Pitch Lists and Coverage Groups in the app). The explainer videos in
// tooling/video read the same numbers, so change them together. Prose (notes,
// titles, labels) lives in `marketing.mediaList.widgets` and
// `marketing.coverageReport.widgets`; these fixtures keep only proper nouns,
// domains, URLs and numbers, and point at the prose by key.

export type PitchStatusKey = "target" | "pitched" | "placed" | "declined";

/** AIPVS / AIPQS tier number (1 Premium … 4 Limited), as the product stores it. */
export type TierNumber = 1 | 2 | 3 | 4;

/** Keys into `marketing.mediaList.widgets.notes`. */
export type PitchNoteKey = "longtrailReview" | "summitWeekly" | "basecampGear";

export interface PitchRowFx {
	id: string;
	name: string;
	domain: string;
	/** Est. Traffic, already compacted the way the table prints it. */
	traffic: string;
	domainRank: number;
	aipvs: { score: number; tier: TierNumber };
	/** The outlet blocks AI crawlers: big numbers, invisible to AI. */
	blocksAi: boolean;
	/** AI citations for the brand's tracked prompts, last 90 days. */
	citations: number;
	status: PitchStatusKey;
	/** The team's note on the row, when it has one. */
	note?: PitchNoteKey;
}

export const pitchList = {
	brand: "Acme Outdoor Co.",
	/** The possessive form the table footnote uses ("Acme's prompts"). */
	brandShort: "Acme",
	windowDays: 90,
	agency: { name: "Northstar PR", initials: "NP" },
};

/** Sorted by Est. Traffic, the way a PR team's list usually starts. */
export const pitchRows: PitchRowFx[] = [
	{
		id: "daily-tamarack",
		name: "The Daily Tamarack",
		domain: "dailytamarack.example",
		traffic: "14.2M",
		domainRank: 93,
		aipvs: { score: 42, tier: 3 },
		blocksAi: true,
		citations: 0,
		status: "target",
	},
	{
		id: "summit-weekly",
		name: "Summit Weekly",
		domain: "summitweekly.example",
		traffic: "2.4M",
		domainRank: 78,
		aipvs: { score: 61, tier: 2 },
		blocksAi: false,
		citations: 7,
		status: "pitched",
		note: "summitWeekly",
	},
	{
		id: "longtrail-review",
		name: "Longtrail Review",
		domain: "longtrailreview.example",
		traffic: "1.8M",
		domainRank: 71,
		aipvs: { score: 84, tier: 1 },
		blocksAi: false,
		citations: 23,
		status: "placed",
		note: "longtrailReview",
	},
	{
		id: "basecamp-gear-guide",
		name: "Basecamp Gear Guide",
		domain: "basecampgear.example",
		traffic: "920K",
		domainRank: 64,
		aipvs: { score: 68, tier: 2 },
		blocksAi: false,
		citations: 11,
		status: "pitched",
		note: "basecampGear",
	},
	{
		id: "alpine-dispatch",
		name: "Alpine Dispatch",
		domain: "alpinedispatch.example",
		traffic: "640K",
		domainRank: 58,
		aipvs: { score: 55, tier: 2 },
		blocksAi: false,
		citations: 0,
		status: "declined",
	},
	{
		id: "ridge-and-route",
		name: "Ridge & Route",
		domain: "ridgeandroute.example",
		traffic: "310K",
		domainRank: 52,
		aipvs: { score: 44, tier: 3 },
		blocksAi: false,
		citations: 3,
		status: "target",
	},
];

/** The four summary tiles above the table (and on the shared page). */
export const pitchSummary = {
	publishers: 6,
	alreadyCitedPct: 67,
	alreadyCited: 4,
	placed: 1,
	premiumStrong: 4,
};

/** Status counts for the Work tile's pipeline. */
export const pitchStatusCounts: Record<PitchStatusKey, number> = {
	target: 2,
	pitched: 2,
	placed: 1,
	declined: 1,
};

/** The scored placement behind the Placed nudge on the Summit Weekly row. */
export const placedNudge = {
	rowId: "summit-weekly",
	url: "summitweekly.example/gear/spring-shelters",
	aipqs: 63,
};

/** Outlets AI cites for competitors' prompts but not for Acme (Build tile). */
export const earnedMediaGaps: Array<{
	domain: string;
	name: string;
	citesFor: string;
	aipvs: { score: number; tier: TierNumber };
}> = [
	{
		domain: "campfirejournal.example",
		name: "Campfire Journal",
		citesFor: "SummitGear, TrailForge",
		aipvs: { score: 72, tier: 2 },
	},
	{
		domain: "backcountrybench.example",
		name: "Backcountry Bench",
		citesFor: "Peakline",
		aipvs: { score: 77, tier: 1 },
	},
	{
		domain: "packlightlab.example",
		name: "Packlight Lab",
		citesFor: "SummitGear",
		aipvs: { score: 58, tier: 2 },
	},
];

/** The "Spring launch coverage" group (name in the widgets namespace). */
export const coverageGroup = {
	urls: 32,
	citations: 31,
	/** Share of the project's AI citations that point to the group. */
	influencePct: 9,
	/** 31 of the project's 340 citations. */
	projectCitations: 340,
	/** Rows shown in the Score tile, of `urls`. */
	shownRows: 5,
};

/** Keys into `marketing.coverageReport.widgets.importNotes`. */
export type CoverageImportNoteKey =
	| "tracking"
	| "noHttps"
	| "syndicated"
	| "shortLink";

/** What the importer does to pasted lines (Import tile). */
export const coverageImportLines: Array<{
	raw: string;
	clean: string;
	note: CoverageImportNoteKey;
}> = [
	{
		raw: "https://www.longtrailreview.example/ridgeline-2-review?utm_source=newsletter",
		clean: "longtrailreview.example/ridgeline-2-review",
		note: "tracking",
	},
	{
		raw: "basecampgear.example/best-tents-2026/",
		clean: "basecampgear.example/best-tents-2026",
		note: "noHttps",
	},
	{
		raw: "https://www.newsstream.example/outdoors/spring-shelters-summit-weekly",
		clean: "newsstream.example/outdoors/spring-shelters-summit-weekly",
		note: "syndicated",
	},
	{
		raw: "https://youtu.be/Rg2FieldTest",
		clean: "youtube.com/watch?v=Rg2FieldTest",
		note: "shortLink",
	},
];

export const coverageImportResult = { added: 32, morePasted: 28 };

/** Keys into `marketing.coverageReport.widgets.articles`. */
export type CoverageArticleKey =
	| "ridgelineReview"
	| "bestTents"
	| "springSheltersSyndicated"
	| "springShelters"
	| "highWind";

/**
 * Scored rows (Score tile), matching the coverage-groups video. Media type +
 * content format, as classified; AIPQS for the article, AIPVS for its host.
 */
export const coverageRows: Array<{
	title: CoverageArticleKey;
	url: string;
	publisher: string;
	mediaType: "press" | "articleBlog";
	format: "review" | "listicle" | "news" | "howTo";
	aipqs: { score: number; tier: TierNumber };
	aipvs: { score: number; tier: TierNumber };
	cited: number;
}> = [
	{
		title: "ridgelineReview",
		url: "longtrailreview.example/ridgeline-2-review",
		publisher: "Longtrail Review",
		mediaType: "press",
		format: "review",
		aipqs: { score: 82, tier: 1 },
		aipvs: { score: 84, tier: 1 },
		cited: 12,
	},
	{
		title: "bestTents",
		url: "basecampgear.example/best-tents-2026",
		publisher: "Basecamp Gear Guide",
		mediaType: "press",
		format: "listicle",
		aipqs: { score: 74, tier: 2 },
		aipvs: { score: 68, tier: 2 },
		cited: 7,
	},
	{
		title: "springSheltersSyndicated",
		url: "newsstream.example/outdoors/spring-shelters-summit-weekly",
		publisher: "Newsstream",
		mediaType: "press",
		format: "news",
		aipqs: { score: 66, tier: 2 },
		aipvs: { score: 72, tier: 2 },
		cited: 4,
	},
	{
		title: "springShelters",
		url: "summitweekly.example/gear/spring-shelters",
		publisher: "Summit Weekly",
		mediaType: "press",
		format: "news",
		aipqs: { score: 63, tier: 2 },
		aipvs: { score: 61, tier: 2 },
		cited: 3,
	},
	{
		title: "highWind",
		url: "ridgeandroute.example/pitching-a-tent-in-high-wind",
		publisher: "Ridge & Route",
		mediaType: "articleBlog",
		format: "howTo",
		aipqs: { score: 41, tier: 3 },
		aipvs: { score: 44, tier: 3 },
		cited: 0,
	},
];

/** One cited-inline hit (Proof tile); its prose is in the widgets namespace. */
export const coverageCitationHit = {
	platform: "ChatGPT",
	platformLogo: "/images/chatgpt-logo.svg",
	url: "longtrailreview.example/ridgeline-2-review",
};

/** Cited vs not-cited comparison strip (Proof tile). 14 cited, 18 not. */
export const citedVsNot: {
	cited: number;
	notCited: number;
	rows: Array<{
		key: "aipqs" | "words" | "byline";
		cited: number;
		notCited: number;
		max: number;
		format: (v: number) => string;
	}>;
} = {
	cited: 14,
	notCited: 18,
	rows: [
		{
			key: "aipqs",
			cited: 71,
			notCited: 48,
			max: 100,
			format: (v) => String(v),
		},
		{
			key: "words",
			cited: 1840,
			notCited: 910,
			max: 2000,
			// Pinned to en-US like every other bento number, so the tile
			// renders identically in both locales (and on server and client).
			format: (v) => v.toLocaleString("en-US"),
		},
		{
			key: "byline",
			cited: 93,
			notCited: 61,
			max: 100,
			format: (v) => `${v}%`,
		},
	],
};

/**
 * Goals on the same PR project, for the Influence tile. The coverage goal's
 * label is the group name; the others are proper nouns.
 */
export const projectGoals: Array<{
	kind: "coverage" | "publisher" | "author";
	label: string | null;
	citations: number;
	pct: number;
}> = [
	{ kind: "coverage", label: null, citations: 31, pct: 9 },
	{ kind: "publisher", label: "Longtrail Review", citations: 12, pct: 4 },
	{ kind: "author", label: "Riley Chen", citations: 9, pct: 3 },
];

/** The /for-pr-firms mini table: placed, in flight, and the blind spot. */
export const prWorkflowMiniRows = [
	"longtrail-review",
	"summit-weekly",
	"daily-tamarack",
];
